import BreadCrumbs from '../../../components/common/BreadCrumbs/BreadCrumbs'
import TimeOffBalance from '../TimeOffBalance'
import { EmployeeInfo } from '../EmployeeInfo'
import React from 'react'
import { useNavigate, useLocation, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import CurrentRequestsList from './CurrentRequestsList'
import Datepicker from '../../../components/common/DatePicker/Datepicker'
import AddTimeToRequest from './AddTimeToRequest'
import ValidationBanner from '../../../components/common/ValidationBanner'
import ColorLegendTooltip from '../../../components/common/ColorLegendTooltip'
import ValidationMessages from '../../../components/common/Validation/validationMessageConfig'
import { getOtherReqDetails } from '../../../store/slices/Requestor/createNewRequestSlice'
import { getBalance } from '../../../store/slices/Requestor/employeeInfoSlice'
import { getTimeOffTypeLabel } from '../../../utils/timeOffTypes'
import { useProfileDisplay } from '../../../hooks/useProfileDisplay'
import { RootState, AppDispatch } from '../../../store/store'
import { useAppSelector } from '../../../store/hooks'
import ManualTimeOffBanner from '../../../components/common/ManualTimeOffBanner';
import {
  toPerRequestBreakdownEntries,
  type OtherRequestBreakdownEntry,
} from '../StatusViews/detail/constants'
import { mapCommonValidationMessage } from '../validation/mapCommonValidationMessage'

const {
  EDUCATION_COMMENT_REQUIRED,
  FLOAT_BALANCE_UNAVAILABLE,
  FLOAT_BALANCE_INFO,
  PARTIAL_DAY_OVERLAP,
  REVISE_REQUEST,
  OTHER_DURATION,
  TOTAL_HOURS_EXCEED_MAX,
} = ValidationMessages

interface ValidationErrorBannersProps {
  messages: string[]
  onFocusOtherSubtype: () => void
}

const mapValidationMessage = (
  text: string,
  onFocusOtherSubtype: () => void
) => {
  const commonValidation = mapCommonValidationMessage(
    text,
    onFocusOtherSubtype
  )
  if (commonValidation) return commonValidation

  if (text === OTHER_DURATION) {
    return {
      heading: <p className="p1 -medium m-0!">{OTHER_DURATION}</p>,
      showMessage: true,
      messageNode: <p className="p3 -book m-0!">{REVISE_REQUEST}</p>,
    }
  }

  if (text === EDUCATION_COMMENT_REQUIRED) {
    return {
      heading: (
        <p className="p1 -medium m-0!">Education Time requires a Comment.</p>
      ),
      showMessage: false,
    }
  }

  if (text === PARTIAL_DAY_OVERLAP) {
    return {
      heading: (
        <p className="p1 -medium m-0!">Partial day requests overlap.</p>
      ),
      showMessage: true,
      messageNode: <p className="p3 -book m-0!">{REVISE_REQUEST}</p>,
    }
  }

  if (text === TOTAL_HOURS_EXCEED_MAX) {
    return {
      heading: <p className="p1 -medium m-0!">{TOTAL_HOURS_EXCEED_MAX}</p>,
      showMessage: false,
    }
  }

  return {
    heading: <p className="p1 -medium m-0!">Error</p>,
    showMessage: true,
    messageNode: <p className="p3 -book m-0!">{text}</p>,
  }
}

const ValidationErrorBanners: React.FC<ValidationErrorBannersProps> = ({
  messages,
  onFocusOtherSubtype,
}) => (
  <div className="space-y-3 mb-4 mt-8!">
    {messages.map((msg, idx) => {
      const { heading, showMessage, messageNode, variant } =
        mapValidationMessage(msg, onFocusOtherSubtype)
      const bannerKey =
        typeof msg === 'string' && msg.length > 0
          ? msg
          : `validation-${idx}`
      return (
        <ValidationBanner
          key={bannerKey}
          variant={variant ?? 'error'}
          {...(heading ? { heading } : {})}
          {...(showMessage && messageNode ? { message: messageNode } : {})}
          compact
        />
      )
    })}
  </div>
)

const normalizeSelectedDates = (value: string | string[]): string[] => {
  if (Array.isArray(value)) return value
  if (value) return [value]
  return []
}

/**
 * Checks if any detail in the array contains a birthday allocation (toTypeCd === 'B').
 */
const hasBirthdayAllocation = (detailsArray: any[]): boolean =>
  detailsArray.some((detail) =>
    (detail.timeOffAllocations || []).some(
      (alloc: any) => alloc.toTypeCd === 'B'
    )
  )

/**
 * Transforms allocations array into tooltip-friendly format.
 */
const buildTooltipAllocations = (
  allocations: any[],
  isBirthdayRequest: boolean
): Array<{ typeLabel: string; hours: number }> =>
  (allocations || [])
    .filter((alloc: any) => alloc?.toTypeCd)
    .map((alloc: any) => ({
      typeLabel: getTimeOffTypeLabel(alloc.toTypeCd),
      hours: isBirthdayRequest ? 8 : Number(alloc.hours) || 0,
    }))

/**
 * Sums up total hours from tooltip allocations array.
 */
const sumAllocationHours = (
  allocations: Array<{ typeLabel: string; hours: number }>
): number => allocations.reduce((sum, alloc) => sum + alloc.hours, 0)

/**
 * Builds individual request entries for multi-request partial days.
 */
const buildIndividualRequests = (
  detailsArray: any[],
  isBirthdayRequest: boolean
): any[] =>
  detailsArray.map((detail) => {
    const detailAllocations = buildTooltipAllocations(
      detail.timeOffAllocations,
      isBirthdayRequest
    )
    const detailTotalHours = isBirthdayRequest
      ? 8
      : sumAllocationHours(detailAllocations)
    return {
      startTime: detail.startTime,
      endTime: detail.endTime,
      allocations: detailAllocations,
      totalHours: detailTotalHours,
    }
  })

const CreateNewRequest: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { mode } = useParams<{ mode?: string }>()
  const dispatch = useDispatch<AppDispatch>()
  const { userInfo, primaryApproverInfo, userNuid } = useProfileDisplay()
  const otherReqData = useSelector(
    (state: RootState) => state.otherReqDetails.data
  )
  const timeOffBalanceData = useSelector(
    (state: RootState) => state.employeeInfo.timeOffBalance.data
  )
  const profileEmpType = useAppSelector(
    (state) => state.profile?.data?.employee?.empType
  )
  // Scoped to the employee the request is for (x-emp-nuid), so this is the
  // selected employee in Manual Time Off rather than the logged-in admin.
  const employeeEmpType = useAppSelector(
    (state) => state.employeeInfo?.employeeInfo?.data?.empType
  )
  const [isAddingMoreDates, setIsAddingMoreDates] = React.useState(false)

  const isExempt = (employeeEmpType ?? profileEmpType) === 'S'
  // Determine mode from URL parameter
  const isAdminMode = mode === 'admin'

  // Get employee data from location state (for admin mode)
  const locationState = location.state as {
    employeeId?: string
    employeeName?: string
    editMode?: boolean
    editDate?: string
    selectedDates?: string[]
  } | null

  const employeeId = locationState?.employeeId
  const employeeName = locationState?.employeeName

  const [selectedDates, setSelectedDates] = React.useState<string[]>([])
  const [hasAnyDateSelected, setHasAnyDateSelected] = React.useState(false)
  const [validationErrorMessages, setValidationErrorMessages] = React.useState<
    string[] | null
  >(null)
  // Track whether AddTimeToRequest is currently showing the review table
  const [isAddTimeReviewVisible, setIsAddTimeReviewVisible] =
    React.useState(false)
  // Counter parent increments to signal child to exit review mode
  const [exitAddTimeReviewSignal, setExitAddTimeReviewSignal] =
    React.useState(0)
  // Counter parent increments to request focus/highlight of "Other Types of Time Off" dropdown.
  const [focusOtherSubtypeSignal, setFocusOtherSubtypeSignal] =
    React.useState(0)
  // Counter bumped on Cancel to force the Datepicker to remount. selectedDates
  // alone resetting to [] isn't enough to move the calendar back to today's
  // month if the user had navigated it elsewhere first — a fresh mount
  // re-initialises the calendar from `value` (now empty), which lands on
  // today, matching the viewedMonth/viewedYear reset below.
  const [calendarResetSignal, setCalendarResetSignal] = React.useState(0)
  // Edit mode state
  const [editMode, setEditMode] = React.useState(false)
  const [editDate, setEditDate] = React.useState<string | null>(null)
  // Dates that have been through a "Next" review cycle. Frozen on each Next click.
  // Used to visually distinguish reviewed (light blue) vs new (dark blue) dates in the datepicker.
  const [committedDates, setCommittedDates] = React.useState<string[]>([])
  // Tracks the last-seen review state to guard against repeated committedDates snapshots.
  // handleAddTimeReviewChange is recreated on every render (no useCallback), causing
  // AddTimeToRequest's effect to re-fire on every parent re-render. Without this ref,
  // setCommittedDates would be called with the stale (already-updated) selectedDates,
  // overwriting the frozen snapshot and making ALL dates appear dark blue.
  const lastReviewStateRef = React.useRef(false)

  // Available balances - typed to allow numeric values or API sentinel strings like 'NA'
  const [balances] = React.useState<{
    vacation: number | string
    float: number | string
    flexible: number | string
    other: number | string
  }>({
    vacation: 60,
    float: 60,
    flexible: 60,
    other: 60,
  })
  // Show info banner only when float balance is explicitly "NA" and user has selected dates
  const showFloatInfo = React.useMemo(() => {
    const v = balances.float
    return (
      hasAnyDateSelected && typeof v === 'string' && v.toUpperCase() === 'NA'
    )
  }, [balances, hasAnyDateSelected])

  // Track viewed month/year for calendar navigation
  const [viewedMonth, setViewedMonth] = React.useState(() =>
    new Date().getMonth()
  )
  const [viewedYear, setViewedYear] = React.useState(() =>
    new Date().getFullYear()
  )

  // Convert YYYY-MM-DD keys from API → MM/DD/YYYY for the Datepicker
  const otherRequestDates = React.useMemo(() => {
    return Object.keys(otherReqData).map((dateStr) => {
      const [year, month, day] = dateStr.split('-')
      return `${month}/${day}/${year}`
    })
  }, [otherReqData])

  // Transform API data to tooltip format (use filtered data)
  const otherRequestDetailsForTooltip = React.useMemo(() => {
    const result: Record<
      string,
      {
        durationLabel: string
        date: string
        allocations: Array<{ typeLabel: string; hours: number }>
        totalHours: number
        startTime?: string
        endTime?: string
        hasMultipleRequests?: boolean
        individualRequests?: any[]
      }
    > = {}

    Object.entries(otherReqData).forEach(([dateKey, detailsArray]) => {
      if (!Array.isArray(detailsArray) || detailsArray.length === 0) return

      const [year, month, day] = dateKey.split('-')
      const formattedDate = `${month}/${day}/${year}`

      // Use FIRST detail only for tooltip display
      const firstDetail = detailsArray[0]
      const isBirthdayRequest = hasBirthdayAllocation(detailsArray)

      // Include every allocation of the first detail in the tooltip summary so
      // that multiple time-off types entered for the same day (e.g. PTO +
      // Float + Other) are all represented. Reading only the first allocation
      // dropped every type added after the first and undercounted the total.
      const tooltipAllocations = buildTooltipAllocations(
        firstDetail.timeOffAllocations,
        isBirthdayRequest
      )

      const tooltipTotalHours = isBirthdayRequest
        ? 8
        : sumAllocationHours(tooltipAllocations)

      const durationLabel =
        firstDetail.durationFlag === 'F' ? 'Full Day' : 'Partial Day'

      const entry: any = {
        durationLabel,
        date: formattedDate,
        allocations: tooltipAllocations,
        totalHours: tooltipTotalHours,
        ...(firstDetail.startTime ? { startTime: firstDetail.startTime } : {}),
        ...(firstDetail.endTime ? { endTime: firstDetail.endTime } : {}),
      }

      // Add flag for multiple requests and build individual requests for modal
      const hasMultipleRequestsFlag =
        (durationLabel === 'Partial Day' && detailsArray.length > 1) ||
        (durationLabel === 'Partial Day' &&
          firstDetail.timeOffAllocations.length > 1)

      if (hasMultipleRequestsFlag) {
        entry.hasMultipleRequests = true

        // Build individualRequests from multiple details if they exist
        if (detailsArray.length > 1) {
          entry.individualRequests = buildIndividualRequests(
            detailsArray,
            isBirthdayRequest
          )
        }
      }

      result[dateKey] = entry
    })

    return result
  }, [otherReqData])

  const otherRequestHoursByDate = React.useMemo(() => {
    const result: Record<string, OtherRequestBreakdownEntry[]> = {}

    Object.entries(otherReqData).forEach(([dateKey, detailsArray]) => {
      const [year, month, day] = dateKey.split('-')
      const mmddyyyy = `${month}/${day}/${year}`
      result[mmddyyyy] = Array.isArray(detailsArray) && detailsArray.length > 0
        ? toPerRequestBreakdownEntries(detailsArray)
        : []
    })

    return result
  }, [otherReqData])

  // Map of MM/DD/YYYY dates that have another user's partial-day request, keyed to an array of
  // ALL their time ranges on that date. Passed to AddTimeToRequest so it can force Partial Day
  // and check for time overlaps against every existing partial request, not just the first one.
  const otherPartialDayDates = React.useMemo(() => {
    const result: Record<
      string,
      Array<{ startTime?: string | null; endTime?: string | null }>
    > = {}
    Object.entries(otherRequestDetailsForTooltip).forEach(
      ([dateKey, entry]) => {
        if (entry.durationLabel === 'Partial Day') {
          const [yr, mo, dy] = dateKey.split('-')
          const mmddyyyy = `${mo}/${dy}/${yr}`
          // When the date has multiple individual requests, collect all their time
          // ranges so the overlap check fires for every occupied window, not just
          // the first. Fall back to the single top-level range when there is only one.
          const ranges: Array<{
            startTime?: string | null
            endTime?: string | null
          }> =
            entry.individualRequests && entry.individualRequests.length > 0
              ? entry.individualRequests.map((req: any) => ({
                  startTime: req.startTime ?? null,
                  endTime: req.endTime ?? null,
                }))
              : [
                  {
                    startTime: entry.startTime ?? null,
                    endTime: entry.endTime ?? null,
                  },
                ]
          result[mmddyyyy] = ranges
        }
      }
    )
    return result
  }, [otherRequestDetailsForTooltip])

  // Fetch other request details when month/year changes (including on mount)
  React.useEffect(() => {
    dispatch(getOtherReqDetails({ month: viewedMonth, year: viewedYear }))
  }, [viewedMonth, viewedYear, dispatch])

  // Fetch time off balance data on component mount (if not already cached in Redux)
  React.useEffect(() => {
    if (!timeOffBalanceData) {
      dispatch(getBalance())
    }
  }, [timeOffBalanceData, dispatch])

  // Handle month/year change from calendar navigation
  const handleMonthYearChange = (month: number, year: number) => {
    setViewedMonth(month)
    setViewedYear(year)
  }

  const handleBreadcrumbPrevious = () => {
    // Build path dynamically based on mode parameter
    const basePath = mode ? `/${mode}/requestor` : '/requestor'
    navigate(basePath, {
      state: isAdminMode
        ? {
            employeeId,
            employeeName,
          }
        : undefined,
    })
  }

  // When navigated back from ReviewRequest with edit intent, initialize selected dates
  React.useEffect(() => {
    const st = location.state || {}
    if (st?.editMode) {
      let incomingDates: string[] = []
      if (Array.isArray(st.selectedDates)) {
        incomingDates = st.selectedDates
      } else if (st.selectedDates) {
        incomingDates = [st.selectedDates]
      }
      setSelectedDates(incomingDates)
      setHasAnyDateSelected(incomingDates.length > 0)
      // Set edit mode and date
      setEditMode(true)
      setEditDate(st.editDate || null)
      // clear validation messages
      setValidationErrorMessages(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state])

  // Handle when a specific date is clicked in the calendar
  const handleDateClick = (clickedDate: string) => {
    const isSelectedDate = selectedDates.includes(clickedDate)
    const isCommittedDate = committedDates.includes(clickedDate)
    // Committed date clicked in post-review add mode: open edit mode without exiting review
    // (review is already exited; the date just needs its hours edited).
    if (
      isCommittedDate &&
      isSelectedDate &&
      !isAddTimeReviewVisible &&
      !isAddingMoreDates
    ) {
      // Drop any newly added (uncommitted) dates before switching back to a
      // committed-date edit, so temporary add-flow dates do not inherit/edit
      // committed-date hours.
      const committedStillSelected = selectedDates.filter((d) =>
        committedDates.includes(d)
      )
      if (committedStillSelected.length !== selectedDates.length) {
        setSelectedDates(committedStillSelected)
        setHasAnyDateSelected(committedStillSelected.length > 0)
      }
      setEditMode(true)
      setEditDate(clickedDate)
      setValidationErrorMessages(null)
      return
    }

    // In review/edit interaction, clicking a selected date should always open/switch form edit mode.
    // When adding more dates, existing dates should not enter edit mode.
    // Only allow selecting new dates.
    if (isAddingMoreDates && isSelectedDate && isCommittedDate) {
      return // still block committed dates
    }
    if (isAddingMoreDates && isSelectedDate && !isCommittedDate) {
      // Toggle off: clicking an already-added new date a second time deselects it.
      const newDates = selectedDates.filter((d) => d !== clickedDate)
      setSelectedDates(newDates)
      setHasAnyDateSelected(newDates.length > 0)
      if (editDate === clickedDate) {
        setEditMode(false)
        setEditDate(null)
      }
      setValidationErrorMessages(null)
      return
    }

    if ((isAddTimeReviewVisible || editMode) && isSelectedDate) {
      setEditMode(true)
      setEditDate(clickedDate)

      if (isAddTimeReviewVisible) {
        setExitAddTimeReviewSignal((c) => c + 1)
      }

      setValidationErrorMessages(null)
    }
  }

  // Returns true when edit-mode fully handled the change (caller should return early).
  const handleEditModeCase = (newSelected: string[]): boolean => {
    if (!editMode || !editDate) return false

    // If selections are unchanged, user clicked an existing date — no action needed
    const compare = (a: string, b: string) => a.localeCompare(b)
    const isUnchanged =
      JSON.stringify([...newSelected].sort(compare)) ===
      JSON.stringify([...selectedDates].sort(compare))
    if (isUnchanged) return true

    // If a date was removed while in edit mode, let normal flow handle cleanup.
    if (newSelected.length < selectedDates.length) {
      return false
    }

    // User added a date while editMode was still active.
    // This can happen after deselecting a partial-day date where editMode/editDate
    // were not fully cleared.
    if (newSelected.length > selectedDates.length) {
      // Only reassign the active edit target when this is a genuine edit of an
      // already-reviewed/committed entry. editMode can also be true because a
      // fresh, not-yet-committed date auto-entered edit mode (e.g. it conflicts
      // with another employee's partial-day request) — in that case adding a
      // second date must be treated as a normal Create addition, not folded
      // into an edit session, otherwise the form gets stuck showing "Edit your
      // request" and per-date validation only ever checks the original date.
      const isGenuineEditSession =
        isAddTimeReviewVisible || committedDates.includes(editDate)
      if (!isGenuineEditSession) {
        return false
      }

      const newDate = newSelected.find((d) => !selectedDates.includes(d))
      if (newDate) {
        setSelectedDates(newSelected)
        setHasAnyDateSelected(newSelected.length > 0)
        setExitAddTimeReviewSignal((c) => c + 1)
        setValidationErrorMessages(null)
        applyEditModeForNewDate(newDate)
        return true
      }
    }
    return false
  }

  /**
   * Dispatches to the correct edit-mode handler based on whether the date
   * is a half-circle (partial-day) date. Centralises the branch so callers
   * remain free of boolean flag logic.
   */
  const applyEditModeForNewDate = (newDate: string): void => {
    setEditMode(true)
    setEditDate(newDate)
  }

  const handleReviewDateAddition = (newSelected: string[]): boolean => {
    if (
      !isAddTimeReviewVisible ||
      isAddingMoreDates ||
      newSelected.length <= selectedDates.length
    ) {
      return false
    }

    const newDate = newSelected.find((d) => !selectedDates.includes(d))
    if (!newDate) return false

    setExitAddTimeReviewSignal((c) => c + 1)
    setValidationErrorMessages(null)
    applyEditModeForNewDate(newDate)
    return true
  }

  const handleNewDateEditMode = (newSelected: string[]): void => {
    const newlyAddedPartialDate = newSelected.find(
      (d) => !selectedDates.includes(d) && !!otherPartialDayDates[d]
    )

    if (newlyAddedPartialDate && !isAddingMoreDates) {
      applyEditModeForNewDate(newlyAddedPartialDate)
    }

    if (
      !newlyAddedPartialDate &&
      !isAddingMoreDates &&
      editMode &&
      editDate &&
      !committedDates.includes(editDate)
    ) {
      const newDate = newSelected.find((d) => !selectedDates.includes(d))
      if (newDate) applyEditModeForNewDate(newDate)
    }

    if (isAddingMoreDates) {
      const newlyAddedDate = newSelected.find(
        (d) => !selectedDates.includes(d)
      )
      if (newlyAddedDate) applyEditModeForNewDate(newlyAddedDate)
    }
  }

  const handleDateChange = (_name: string, value: string | string[]) => {
    // Normalize value to an array of strings
    const newSelected = normalizeSelectedDates(value)
    const currentlySelectedCommitted = selectedDates.filter((d) =>
      committedDates.includes(d)
    )
    const nextSelectedCommitted = newSelected.filter((d) =>
      committedDates.includes(d)
    )
    if (nextSelectedCommitted.length < currentlySelectedCommitted.length) {
      setValidationErrorMessages(null)
      return
    }
    // Check if any newly added date has 3+ partial day requests
    // While review table is visible, clicking an already-selected date can fire onChange with a
    // temporarily deselected array. Ignore that transition so calendar click opens edit form immediately.
    if (
      isAddTimeReviewVisible &&
      !editMode &&
      newSelected.length < selectedDates.length
    ) {
      return
    }
    if (isAddingMoreDates) {
      const hasRemovedNewDate = selectedDates.some(
        (d) => !committedDates.includes(d) && !newSelected.includes(d)
      )
      // Toggling off an already-added date is handled by handleDateClick, whose
      // onDateClick fires before this onChange callback on the same click — skip
      // here so we don't fight that removal or double-process it.
      if (hasRemovedNewDate) {
        return
      }
    }

    // Handle edit mode: prevent normal date toggling
    if (handleEditModeCase(newSelected)) return

    // Normal mode: standard date selection
    setSelectedDates(newSelected)
    setHasAnyDateSelected(newSelected.length > 0)

    // The date being edited (e.g. a fresh partial-day date that auto-entered
    // edit mode because it conflicts with another employee's request) was
    // just toggled off — exit edit mode instead of leaving a stale editDate
    // that no longer exists in the selection.
    if (editDate && !newSelected.includes(editDate)) {
      setEditMode(false)
      setEditDate(null)
    }

    // Review visible + new date added: exit review and show the form.
    // Clear committedDates so all dates render dark blue (no light-blue
    // distinction between reviewed and new dates in the active-add context).
    // For half-circle dates also enter date-edit mode immediately so
    // AddTimeToRequest can force Partial Day and disable Full Day.
    if (handleReviewDateAddition(newSelected)) return

    // If child is in review, clicking a date indicates intent to edit that date; signal child to exit review
    if (isAddTimeReviewVisible && newSelected.length > 0) {
      setExitAddTimeReviewSignal((c) => c + 1)
    }
    // Always clear validation error whenever dates change
    setValidationErrorMessages(null)

    handleNewDateEditMode(newSelected)
  }

  // Called by child to notify review state changes
  const handleAddTimeReviewChange = (isReview: boolean) => {
    setIsAddTimeReviewVisible(isReview)
    // Snapshot committed dates ONLY on the false→true transition (review becoming visible).
    // This guard is essential: because this callback has no useCallback wrapper it is a new
    // function reference on every render, which causes AddTimeToRequest's effect to re-fire
    // on every parent re-render. Without the guard, setCommittedDates would be called with
    // the already-updated selectedDates (e.g. including the new date just added), erasing
    // the frozen snapshot and making all dates appear dark blue instead of light/dark.
    if (isReview && !lastReviewStateRef.current) {
      setCommittedDates([...selectedDates])
      // Adding-more-dates flow complete: the new date is now committed to the review.
      setIsAddingMoreDates(false)
    }
    lastReviewStateRef.current = isReview
  }

  // Handle removing a date from the request
  const handleRemoveDateClick = (dateToRemove: string) => {
    // Remove the date from selectedDates
    const newDates = selectedDates.filter((d) => d !== dateToRemove)
    setSelectedDates(newDates)
    setHasAnyDateSelected(newDates.length > 0)
    // Remove from committedDates so removed dates no longer appear in light blue
    const newCommittedDates = committedDates.filter((d) => d !== dateToRemove)
    setCommittedDates(newCommittedDates)
    // Exit edit mode and return to calendar
    setEditMode(false)
    setEditDate(null)
  }

  /**
   * Remove multiple dates from the request in a single atomic update.
   * Avoids the stale-closure issue that arises when calling handleRemoveDateClick
   * in a loop (each call would read the same pre-update selectedDates snapshot).
   */
  const handleRemoveDatesClick = (datesToRemove: string[]) => {
    const newDates = selectedDates.filter((d) => !datesToRemove.includes(d))
    setSelectedDates(newDates)
    setHasAnyDateSelected(newDates.length > 0)
    // Remove from committedDates so removed dates no longer appear in light blue
    const newCommittedDates = committedDates.filter(
      (d) => !datesToRemove.includes(d)
    )
    setCommittedDates(newCommittedDates)
    // Unconditionally exiting edit mode here used to also kick the user out of
    // whichever date they were actively editing — including the one call site
    // (Req 3's deselect, handleDurationChange switching Duration to Full Day)
    // where the active editDate is deliberately NOT one of the dates being
    // removed. Only reset edit mode when the active date is itself among the
    // removed dates; otherwise keep the user in its form.
    if (!editDate || datesToRemove.includes(editDate)) {
      setEditMode(false)
      setEditDate(null)
    }
  }

  // Handle exiting edit mode when Update is clicked successfully
  const handleExitEditMode = () => {
    setEditMode(false)
    setEditDate(null)
  }

  // Fully reset create request flow while staying on this page
  const handleCancelCreateRequest = () => {
    setSelectedDates([])
    setHasAnyDateSelected(false)
    setValidationErrorMessages(null)
    setIsAddTimeReviewVisible(false)
    setEditMode(false)
    setEditDate(null)
    setCommittedDates([])
    setIsAddingMoreDates(false)
    lastReviewStateRef.current = false
    setExitAddTimeReviewSignal((c) => c + 1)
    // If the user had navigated the calendar to a different month before
    // cancelling, both the calendar's own displayed month and the
    // "other requests" data cached for it need resetting back to today —
    // otherwise one of the two silently stays stale after Cancel:
    // - calendarResetSignal (bumped below) remounts the Datepicker, which
    //   re-initialises its displayed month from `value` (now []) → today.
    // - viewedMonth/viewedYear reset here re-fires the getOtherReqDetails
    //   fetch effect for today's month, so the remounted calendar actually
    //   has color data to show instead of landing on an empty today.
    setViewedMonth(new Date().getMonth())
    setViewedYear(new Date().getFullYear())
    setCalendarResetSignal((c) => c + 1)
  }
  const handleAddMoreDates = () => {
    setIsAddingMoreDates(true)

    // exit review
    setIsAddTimeReviewVisible(false)
    setExitAddTimeReviewSignal((c) => c + 1)

    // disable edit mode — AddTimeToRequest stays mounted so perDateHoursStorage is preserved
    setEditMode(false)
    setEditDate(null)
    setValidationErrorMessages(null)
  }

  return (
    <div className="page-container min-h-[70vh] lg:mx-40! desktop:mx-[120px]! tablet:mx-5! mobile:mx-4!">
      <BreadCrumbs
        previousPage="TORT"
        currentPage="Create New Time Off Request"
        onPreviousClick={handleBreadcrumbPrevious}
      />

      <h1 className="h1 desktop:mt-[60px]! tablet:mt-[50px]! mobile:mt-8!">
        Create New Time Off Request
      </h1>

      {isAdminMode ? (
        <div className="mb-4">
          <ManualTimeOffBanner />
        </div>
      ) : (
        <>
          <p className="p3 -book mb-0!">You are logged in as:</p>
          <h3 className="h3 mb-0! mt-0!">
            {userInfo} {userNuid}
          </h3>
          <p className="p3 -book mt-0! desktop:mb-2!">
            Primary Approver: {primaryApproverInfo}
          </p>
        </>
      )}

      {showFloatInfo && !isAddTimeReviewVisible && (
        <div className="mb-4">
          <ValidationBanner
            variant="info"
            heading={<p className="p1 -medium">{FLOAT_BALANCE_UNAVAILABLE}</p>}
            message={<p className="p3 -book mt-2">{FLOAT_BALANCE_INFO}</p>}
            compact={false}
          />
        </div>
      )}

      {validationErrorMessages && validationErrorMessages.length > 0 && (
        <ValidationErrorBanners
          messages={validationErrorMessages}
          onFocusOtherSubtype={() =>
            setFocusOtherSubtypeSignal((count) => count + 1)
          }
        />
      )}

      <div className="flex gap-16! tablet:gap-8! mobile:gap-2! mt-4 items-start flex-col tablet:flex-row mobile:flex-col">
        {!isAddTimeReviewVisible && (
          <div className="w-full lg:w-auto">
              <div className="flex items-center">
                <h2 className="h2 mt-0 tablet:mt-0">
                  {isAddTimeReviewVisible ? 'Request Dates' : 'Select Dates'}
                </h2>
                {/* Colour legend tooltip */}
                <div className="ml-2">
                  <ColorLegendTooltip />
                </div>
              </div>
            <Datepicker
              key={calendarResetSignal}
              name="dates"
              label="Select Dates"
              value={selectedDates}
              onChange={handleDateChange}
              onDateClick={handleDateClick}
              onMonthYearChange={handleMonthYearChange}
              required
              openBehaviour="calendarView"
              isEditMode={editMode && !!editDate && !isAddingMoreDates}
              editDate={editDate}
              editModeSelectedDates={selectedDates}
              canEnterEditMode={isAddTimeReviewVisible}
              otherSelectedDatesToShow={otherRequestDates}
              otherRequestDetails={otherRequestDetailsForTooltip}
              committedDates={committedDates}
              calendarWidth={372}
              calendarHeight={399}
               disableCommittedDateClick={isAddingMoreDates}
            />
          </div>
        )}
        <div className="w-full lg:flex-1">
          {hasAnyDateSelected ? (
            <AddTimeToRequest
              selectedDates={selectedDates}
              committedDates={committedDates}
              onValidationError={setValidationErrorMessages}
              focusOtherSubtypeSignal={focusOtherSubtypeSignal}
              onCancelRequest={handleCancelCreateRequest}
              exitAddTimeReviewSignal={exitAddTimeReviewSignal}
              onAddTimeReviewChange={handleAddTimeReviewChange}
              editMode={editMode}
              editDate={editDate}
              onEditDateClick={(date) => {
                setEditDate(date)
                setEditMode(true)
                // Signal child to exit review table and show form
                setExitAddTimeReviewSignal((c) => c + 1)
              }}
              onRemoveDateClick={handleRemoveDateClick}
              onExitEditMode={handleExitEditMode}
              onBackFromEditMode={handleExitEditMode}
              employeeId={employeeId}
              employeeName={employeeName}
              otherPartialDayDates={otherPartialDayDates}
              otherRequestHoursByDate={otherRequestHoursByDate}
              onRemoveDatesClick={handleRemoveDatesClick}
              isExempt={isExempt}
              isAddingMoreDates={isAddingMoreDates}
              onAddMoreDates={handleAddMoreDates}
            />
          ) : (
            
            <p className="p3 -medium pt-2 tablet:pt-4 mobile:pt-2">
              Select a date on the Calendar to add time to your request
            </p>
          )}
        </div>
        <div>
        </div>
      </div>
      {!hasAnyDateSelected && (
        <>
          <h2 className="h2 mt-8 mb-4">Current Requests</h2>
          <CurrentRequestsList />
        </>
      )}
      <TimeOffBalance />
      <EmployeeInfo />
    </div>
  )
}

export default CreateNewRequest
